/**
 * 手动加减积分（与 iOS Home/AdjustPointsSheet.swift 一致），首页和成就页共用：
 *   <AdjustPointsSheet open={open} onClose={() => setOpen(false)} childId={child.id} />
 * 网页版没有生物验证，提交前用 confirm() 二次确认。
 */
import { Star } from "lucide-react";
import { useState } from "react";
import { confirm, run, writeEntity, useStore } from "../core";
import { Button, ChoiceChip, NumberStepper, PillSegment, Sheet, SheetBody, SheetHeader, TextField } from "../ui";
import "./AdjustPointsSheet.css";

const REASONS = ["主动做家务", "表现很棒", "按时睡觉", "帮助别人", "没遵守约定"];

export function AdjustPointsSheet({ open, onClose, childId }: { open: boolean; onClose: () => void; childId: string }) {
  const name = useStore((s) => s.children[childId]?.name ?? "");
  return (
    <Sheet open={open} onClose={onClose} width={480} label="调整积分">
      <SheetHeader title={`调整${name}的积分`} onClose={onClose} />
      <AdjustPointsBody childId={childId} onDone={onClose} />
    </Sheet>
  );
}

function AdjustPointsBody({ childId, onDone }: { childId: string; onDone: () => void }) {
  const child = useStore((s) => s.children[childId]);
  const [amount, setAmount] = useState(5);
  const [add, setAdd] = useState(true);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const trimmed = reason.trim();

  async function submit() {
    if (!child || !trimmed) return;
    const ok = await confirm({
      title: `${add ? "给" : "扣"}${child.name} ${amount} 分？`,
      message: `原因：${trimmed}（孩子能看到）`,
      confirmText: add ? "加分" : "扣分",
      danger: !add,
    });
    if (!ok) return;
    setBusy(true);
    const r = await run(
      () => writeEntity("pointLedger", "POST", `children/${child.id}/points/adjust`, { delta: add ? amount : -amount, reason: trimmed }),
      add ? `已加 ${amount} 分` : `已扣 ${amount} 分`,
    );
    setBusy(false);
    if (r) onDone();
  }

  if (!child) return null;
  return (
    <SheetBody gap={16}>
      <div className="adjust-balance">
        <span>当前余额</span>
        <strong className="kp-num">{child.pointsBalance}</strong>
        <span className="adjust-spacer" />
        <PillSegment
          items={[
            [1, "加分"],
            [0, "扣分"],
          ]}
          value={add ? 1 : 0}
          onChange={(v) => setAdd(v === 1)}
          background="var(--kp-surface)"
        />
      </div>
      <div className="adjust-amount">
        <span>{add ? "加" : "扣"}</span>
        <NumberStepper value={amount} onChange={setAmount} min={1} max={1000} />
      </div>
      <div className="adjust-reason">
        <TextField label="原因（孩子能看到）" placeholder="必须填写原因" value={reason} onChange={setReason} maxLength={100} />
        <div className="adjust-chips">
          {REASONS.map((r) => (
            <ChoiceChip key={r} selected={reason === r} onClick={() => setReason(r)} unselectedBg="var(--kp-surface)">
              {r}
            </ChoiceChip>
          ))}
        </div>
      </div>
      <Button block busy={busy} disabled={!trimmed} icon={<Star size={20} strokeWidth={2.6} />} onClick={() => void submit()}>
        {`${add ? "加" : "扣"} ${amount} 分`}
      </Button>
    </SheetBody>
  );
}
